<template>
  <div :class="cn(feedItemVariants({ variant }), props.class)">
    <div class="flex gap-3 items-center">
      <Avatar
        size="sm"
        :name="item.user?.fullname"
        :src="item.user?.avatar_url"
        :initials="item.user?.avatar_initials"
      />
      <div class="flex gap-2 w-full">
        <div class="flex flex-wrap w-full items-center gap-x-2">
          <i18n-t
            scope="global"
            tag="div"
            class="text-style-xs text-primary"
            :keypath="
              [
                FeedTypes.ACTIVITY_LOG,
                FeedTypes.TIME_LOG,
                FeedTypes.MATERIAL_LOG,
              ].includes(variant)
                ? 'tasks.activity.logged_by'
                : 'tasks.activity.comment_by'
            "
          >
            <template #causer>
              <span class="text-primary font-medium">
                {{ causer }}
              </span>
            </template>
          </i18n-t>
          <TimeAgo
            v-if="
              [FeedTypes.ACTIVITY_LOG, FeedTypes.COMMENTS].includes(variant)
            "
            relative
            :date="new Date(item.created_at)"
            :locale="locale"
          />
          <span
            v-else-if="[FeedTypes.TIME_LOG].includes(variant)"
            class="text-style-xs text-tertiary grow"
          >
            {{ formatDate(item.created_at) }}
            <span v-if="!maxSmall"
              >, {{ formatTime(item.start, { timeStyle: "short" }) }} -
              {{ formatTime(item.end, { timeStyle: "short" }) }}</span
            >
          </span>
          <span
            v-else-if="[FeedTypes.MATERIAL_LOG].includes(variant)"
            class="text-style-xs text-tertiary grow"
          >
            {{ formatDate(item.created_at) }}
          </span>
          <Badge
            v-if="item.comment?.type === 'internal'"
            :label="t('validation.values.comment.internal')"
            type="light"
            color="gray-blue"
            size="sm"
          />
          <Badge
            v-if="item.comment?.type === 'public_reply'"
            :label="t('validation.values.comment.public')"
            type="light"
            color="indigo"
            size="sm"
          />
          <Badge
            v-if="item.badge"
            :label="item.badge"
            :type="maxSmall ? 'light' : 'modern'"
            :color="maxSmall ? 'blue-light' : 'gray'"
            :size="maxSmall ? 'md' : 'sm'"
            class="align-self-end"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-row gap-3">
      <div class="flex shrink-0 justify-center w-6">
        <div
          :class="{
            'opacity-0': last,
            'w-[1px]': lineSize === 'sm',
            'w-0.5': lineSize === 'md',
            'w-1': lineSize === 'lg',
          }"
          class="h-full bg-utility-gray-300 rounded-3xs"
        />
      </div>
      <BundledActivityLog
        v-if="item.isGrouped"
        :item="item"
        :causer="actualUserName"
        class="w-full"
      />
      <Component
        v-else
        :is="
          item.comment || item.description === 'commented'
            ? MessageBubble
            : ActivityLog
        "
        :item="item"
        v-bind="
          [FeedTypes.MATERIAL_LOG, FeedTypes.TIME_LOG].includes(variant)
            ? {
                class: 'rounded-tl-sm',
              }
            : { task: task }
        "
        v-on="
          item.comment || item.description === 'commented'
            ? { preview: (file) => emit('preview', file) }
            : {}
        "
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import {
  ActivityLog,
  BundledActivityLog,
} from "@/SharedComponents/ActivityLog";
import { Avatar } from "@/SharedComponents/Avatars";
import { Badge } from "@/SharedComponents/Badges";
import { TimeAgo } from "@/SharedComponents/Date";
import { FeedTypes, feedItemVariants } from "@/SharedComponents/Feed";
import { MessageBubble } from "@/SharedComponents/MessageBubble";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useDate } from "@/Composables/useDate";
import { useI18n } from "vue-i18n";
import { cn } from "@/Utils/cn";

const { maxSmall } = useBreakpoints();
const { formatDate, formatTime } = useDate();
const { t, locale } = useI18n();

const emit = defineEmits(["preview"]);

const props = defineProps({
  variant: { type: String, required: true },
  item: { type: Object, required: true },
  task: { type: Object, default: () => {} },
  lineSize: { type: String, default: "md" },
  last: { type: Boolean, default: true },
});

const causer = computed(() => {
  const { messenger, user, description } = props.item;

  if (
    description === "priority_detected" ||
    description === "category_detected"
  ) {
    return t("components.fixform");
  }

  if (user) {
    if (user.id === usePage().props?.auth.user.id) {
      return t("components.you");
    }

    return user.fullname || user.email;
  }

  if (messenger) {
    const name = messenger.name ? `${messenger.name} - ` : null ?? "";

    if (messenger.email && messenger.mobile) {
      return `${name} ${messenger.email} (${messenger.mobile})`.trim();
    }

    if (messenger.email) {
      return `${name} ${messenger.email}`.trim();
    }

    if (messenger.mobile) {
      return `${name} ${messenger.mobile}`.trim();
    }
  }

  if (isRecurringTask.value || isReminder.value) {
    return t("components.fixform");
  }

  return t("components.anonymous");
});

const actualUserName = computed(() => {
  const { messenger, user } = props.item;

  if (user) {
    return user.fullname || user.email;
  }

  if (messenger) {
    const name = messenger.name ? `${messenger.name} - ` : null ?? "";

    if (messenger.email && messenger.mobile) {
      return `${name} ${messenger.email} (${messenger.mobile})`.trim();
    }

    if (messenger.email) {
      return `${name} ${messenger.email}`.trim();
    }

    if (messenger.mobile) {
      return `${name} ${messenger.mobile}`.trim();
    }
  }

  if (isRecurringTask.value || isReminder.value) {
    return t("components.fixform");
  }

  return t("components.anonymous");
});

const isRecurringTask = computed(() => {
  return props.task?.recurrence !== null;
});

const isReminder = computed(() => {
  return props.task?.reminder;
});
</script>
